import { useCurrentCloudProvider, useAccountId } from '@labrav/common'
import {
  StatusBarColorsV3,
  StatusBarV3,
  StatusBarStatusColorsV3,
  ButtonV2,
  LabraToolTipV2,
  StatusBarV4,
  StatusBarStatusColorsV4,
  StatusBarColorsV4,
  ButtonV4,
} from '@labrav/react-components'
import {
  AgreementDurationType,
  ResellerAgreementStatus,
} from '../../../../types/flyout'
import type { ViewResellerAuthorizationType } from '../../../../modules/receivedResellerAuthorization/types'
import { useStyles } from './ResellerAuthorizationHeader.styles'
import { snakeToSentenceCase } from '@labrav/common'
import { productDetailDate } from '../../../../common/utils/productCreationUtil'
import { useTimeZone } from '../../../../common/utils/hooks/useTimeZone'
import { ResellerAuthorizationHeaderActions } from './ResellerAuthorizationHeaderActions'
import { useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch } from '../../../../store'
import { setSelectedActiveReceivedResaleAuthorizations } from '../../../../modules/receivedResellerAuthorization/action'
import { useFlag } from '@labrav/flags'
import clsx from 'clsx'
import { getExpirationFromDate } from '../../../../common/utils/dates'
import { ViewError } from '../../../ViewError/ViewError'
import { getMicrosoftStatus } from './helpers'

export { getMicrosoftStatus } from './helpers'
export { ViewError } from '../../../ViewError/ViewError'

export const ResellerAuthorizationHeader = ({
  resellerAuthorization,
  crmOpportunityId,
  externalBaseURL,
  search,
}: {
  resellerAuthorization: ViewResellerAuthorizationType
  crmOpportunityId?: string
  externalBaseURL?: string
  search?: string
}) => {
  const { classes } = useStyles()
  const { timeZone } = useTimeZone()
  const { accountId: routeAccountId, cloud: routeCloud } = useParams<{
    cloud: string
    accountId: string
  }>()
  const fallbackAccountId = useAccountId({ product: 'flyout' }) || ''
  const accountId = routeAccountId || fallbackAccountId
  const { flag: isMicrosoftResaleAuthorizationsEnabled } = useFlag(
    'microsoftResellerAuthorization'
  )
  const cloudMarketplace = useCurrentCloudProvider({
    microsoftResellerAuthorization:
      isMicrosoftResaleAuthorizationsEnabled.value,
  })
  const { status, agreementOrigin, id, offerStatus } = resellerAuthorization
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const handleCreateCPPO = useCallback(async () => {
    await dispatch(setSelectedActiveReceivedResaleAuthorizations(id))
    await navigate(
      `/flyout/${routeCloud}/${accountId}/authorization/${id}/channel-offers/edit`
    )
  }, [id, routeCloud, accountId])

  const isReceivedResellerAuth = agreementOrigin === 'RECEIVED'
  const statusView = snakeToSentenceCase(status || '')
  const microsoftStatus = getMicrosoftStatus(offerStatus)
  const {
    modifiedDate: resaleAuthExpiredAtDate,
    modifiedTime: resaleAuthExpiredAtTime,
  } = resellerAuthorization?.auditJson?.resaleAuthExpiredAt
    ? productDetailDate(
        resellerAuthorization?.auditJson?.resaleAuthExpiredAt,
        timeZone
      )
    : { modifiedDate: '', modifiedTime: '' }

  const { fullDateTime: offerCreationFailedDate } = resellerAuthorization
    ?.statusLogs?.offerCreationFailed
    ? productDetailDate(
        resellerAuthorization?.statusLogs?.offerCreationFailed,
        timeZone
      )
    : { fullDateTime: '' }

  const { commaSeperatedDateTime: offerPurchaseCompleteDate } =
    resellerAuthorization?.statusLogs?.offerPurchaseComplete
      ? productDetailDate(
          resellerAuthorization?.statusLogs?.offerPurchaseComplete,
          timeZone
        )
      : { commaSeperatedDateTime: '' }

  const { commaSeperatedDateTime: offerAcceptedDate } = resellerAuthorization
    ?.statusLogs?.offerAccepted
    ? productDetailDate(
        resellerAuthorization?.statusLogs?.offerAccepted,
        timeZone
      )
    : { commaSeperatedDateTime: '' }

  const { commaSeperatedDateTime: offerRestrictedDate } = resellerAuthorization
    ?.statusLogs?.offerRestricted
    ? productDetailDate(
        resellerAuthorization?.statusLogs?.offerRestricted,
        timeZone
      )
    : { commaSeperatedDateTime: '' }

  const { modifiedDate: offerExpiredDate } = resellerAuthorization?.statusLogs
    ?.offerExpired
    ? productDetailDate(
        resellerAuthorization?.statusLogs?.offerExpired,
        timeZone
      )
    : { modifiedDate: '' }

  const navigateToAzureCheck = () => {
    window.open(
      'https://helpcenter.labra.io//hc/en-us/articles/40501529247251-Accepting-Private-Offers-on-Azure-Marketplaces-A-Step-by-Step-Guide-for-Procurement-Teams#h_01JS92J7P2E7BY4PB5DGDBG2FY',
      '_blank'
    )
  }

  const { commaSeperatedDateTime: acceptByDate } =
    resellerAuthorization?.acceptBy
      ? productDetailDate(resellerAuthorization?.acceptBy, timeZone)
      : { commaSeperatedDateTime: '' }

  const { isExpriringSoon, dayDiff, isOneDayLeft } = getExpirationFromDate(
    resellerAuthorization?.acceptBy || '',
    5
  )

  const offerExpiringSoon =
    isExpriringSoon &&
    [
      ResellerAgreementStatus.OFFER_SENT_RESELLER,
      ResellerAgreementStatus.OFFER_VIEWED_RESELLER,
      ResellerAgreementStatus.OFFER_EXTENDED_TO_BUYER,
    ].includes(
      offerStatus || ResellerAgreementStatus.OFFER_CREATION_IN_PROGRESS
    )

  const noStatusBar =
    offerStatus &&
    !offerExpiringSoon &&
    [
      ResellerAgreementStatus.OFFER_SENT_RESELLER,
      ResellerAgreementStatus.OFFER_CREATION_IN_PROGRESS,
    ].includes(offerStatus)

  return (
    <div className={classes.statusWrapper} data-testid={'authorization-header'}>
      <div className={clsx(classes.status, noStatusBar && classes.noStatusBar)}>
        {cloudMarketplace === 'AWS' && (
          <>
            {status === ResellerAgreementStatus.IN_PROGRESS && (
              <StatusBarV4
                backgroundColor={StatusBarColorsV4.LIGHTBLUE}
                status={'In progress'}
                divider
                statusColor={StatusBarStatusColorsV4.LIGHTBLUE}
                content={
                  'The authorization is being created in the AWS Marketplace and will be ready in approximately 5 minutes. Please refresh the page periodically to check the status.'
                }
              />
            )}
            {status === ResellerAgreementStatus.DEACATIVATION_IN_PROGRESS && (
              <StatusBarV3
                backgroundColor={StatusBarColorsV3.YELLOW}
                status={statusView}
                statusColor={StatusBarStatusColorsV3.YELLOW}
                divider
                content={
                  'Deactivation is initiated, will be deactivated in 5 min.'
                }
              />
            )}
            {status === ResellerAgreementStatus.COMPLETED && (
              <StatusBarV4
                backgroundColor={StatusBarColorsV4.LIGHTBLUE}
                status={statusView}
                divider
                statusColor={StatusBarStatusColorsV4.LIGHTBLUE}
                content={
                  isReceivedResellerAuth
                    ? 'You created the channel offer, but it’s not yet accepted by the buyer. No further offer creation allowed.'
                    : 'Reseller has created the channel offer, but it’s not yet accepted by the buyer. No further offer creation allowed.'
                }
              />
            )}
            {status === ResellerAgreementStatus.CHANNEL_OFFER_ACCEPTED && (
              <StatusBarV4
                backgroundColor={StatusBarColorsV4.LIGHTBLUE}
                status={statusView}
                divider
                statusColor={StatusBarStatusColorsV4.LIGHTBLUE}
                content={
                  isReceivedResellerAuth
                    ? 'Channel offer has been accepted.'
                    : 'Channel offer created by the reseller has been accepted.'
                }
              />
            )}
            {status === ResellerAgreementStatus.DEACATIVATED && (
              <StatusBarV4
                backgroundColor={StatusBarColorsV4.RED}
                status={statusView}
                divider
                statusColor={StatusBarStatusColorsV4.LIGHTBLUE}
                content={
                  isReceivedResellerAuth
                    ? 'You can no longer create a channel offer.'
                    : 'Reseller can no longer create a channel offer.'
                }
              />
            )}
            {status === ResellerAgreementStatus.ACTIVE && (
              <StatusBarV3
                backgroundColor={StatusBarColorsV3.GREEN}
                status={statusView}
                statusColor={StatusBarStatusColorsV3.GREEN}
                content={
                  isReceivedResellerAuth
                    ? resellerAuthorization.agreementDurationType ===
                      AgreementDurationType.SINGLE_USE.toLowerCase()
                      ? 'You can create only one channel offer.'
                      : 'You can create multiple channel offers.'
                    : resellerAuthorization.agreementDurationType ===
                        AgreementDurationType.SINGLE_USE.toLowerCase()
                      ? 'Reseller can create only one channel offer.'
                      : 'Reseller can create multiple channel offers.'
                }
              />
            )}
            {status === ResellerAgreementStatus.ACTIVE &&
              isReceivedResellerAuth && (
                <div className={classes.channelOffer}>
                  <LabraToolTipV2 tooltipPlacement="bottom" description="">
                    <ButtonV2
                      smallVariant
                      data-testid={'create-channel-offer'}
                      onClick={handleCreateCPPO}
                    >
                      Create channel offer
                    </ButtonV2>
                  </LabraToolTipV2>
                </div>
              )}
            {status === ResellerAgreementStatus.RESTRICTED && (
              <StatusBarV3
                backgroundColor={StatusBarColorsV3.RED}
                status={'Deactivated'}
                divider
                statusColor={StatusBarStatusColorsV3.RED}
                content={
                  isReceivedResellerAuth
                    ? 'You can no longer create a channel offer.'
                    : 'Reseller can no longer create a channel offer.'
                }
              />
            )}
            {status === ResellerAgreementStatus.EXPIRED && (
              <StatusBarV3
                backgroundColor={StatusBarColorsV3.GRAY}
                status={'Ended'}
                divider
                statusColor={StatusBarStatusColorsV3.GRAY}
                content={`Authorization has ended on ${resaleAuthExpiredAtDate} ${resaleAuthExpiredAtTime}.`}
              />
            )}
            {status === ResellerAgreementStatus.FAILED && (
              <StatusBarV3
                backgroundColor={StatusBarColorsV3.RED}
                status={statusView}
                divider
                statusColor={StatusBarStatusColorsV3.RED}
                content={resellerAuthorization?.error}
              />
            )}
          </>
        )}

        {cloudMarketplace === 'AZURE' && (
          <>
            {offerStatus === ResellerAgreementStatus.OFFER_CREATION_FAILED && (
              <StatusBarV4
                backgroundColor={StatusBarColorsV4.RED}
                status={microsoftStatus}
                divider
                statusColor={StatusBarStatusColorsV4.RED}
                content={
                  resellerAuthorization?.errorDetails &&
                  resellerAuthorization?.errorDetails?.errors ? (
                    <div
                      className={classes.errorSubContent}
                      data-testid={'failed-offer-content'}
                    >
                      <div>
                        <ViewError
                          errors={resellerAuthorization?.errorDetails?.errors}
                        />
                      </div>
                      <div className={classes.offerUpdatedAt}>
                        {offerCreationFailedDate}
                      </div>
                    </div>
                  ) : (
                    ''
                  )
                }
              />
            )}
            {!offerExpiringSoon &&
              offerStatus ===
                ResellerAgreementStatus.OFFER_PURCHASE_COMPLETE && (
                <StatusBarV4
                  backgroundColor={StatusBarColorsV4.LIGHTBLUE}
                  status={microsoftStatus}
                  divider
                  statusColor={StatusBarStatusColorsV4.LIGHTBLUE}
                  content={
                    <div>
                      Offer was accepted and subscribed on{' '}
                      {offerPurchaseCompleteDate}. Next step pending from the
                      customer is to configure the account. Check
                      <ButtonV4
                        variant="tertiary"
                        onClick={navigateToAzureCheck}
                        className={classes.btnLink}
                      >
                        here
                      </ButtonV4>
                      for detailed steps
                    </div>
                  }
                />
              )}
            {!offerExpiringSoon &&
              offerStatus === ResellerAgreementStatus.OFFER_VIEWED_RESELLER && (
                <StatusBarV4
                  backgroundColor={StatusBarColorsV4.LIGHTBLUE}
                  status={microsoftStatus}
                  statusColor={StatusBarStatusColorsV4.LIGHTBLUE}
                />
              )}
            {!offerExpiringSoon &&
              offerStatus ===
                ResellerAgreementStatus.OFFER_EXTENDED_TO_BUYER && (
                <StatusBarV4
                  backgroundColor={StatusBarColorsV4.LIGHTBLUE}
                  status={microsoftStatus}
                  divider
                  statusColor={StatusBarStatusColorsV4.LIGHTBLUE}
                  content="Offer has been extended to the buyer by the channel partner."
                />
              )}

            {!offerExpiringSoon &&
              offerStatus ===
                ResellerAgreementStatus.OFFER_TERMS_ACCEPTED_BUYER && (
                <StatusBarV4
                  backgroundColor={StatusBarColorsV4.YELLOW}
                  status={microsoftStatus}
                  statusColor={StatusBarStatusColorsV4.YELLOW}
                  content={'Buyer is yet to create the microsoft subscription.'}
                />
              )}
            {offerStatus === ResellerAgreementStatus.OFFER_ACCEPTED && (
              <StatusBarV4
                backgroundColor={StatusBarColorsV4.GREEN}
                status={microsoftStatus}
                divider
                statusColor={StatusBarStatusColorsV4.GREEN}
                content={`Offer was accepted on ${offerAcceptedDate} and all steps post acceptance are completed.`}
              />
            )}

            {offerStatus ===
              ResellerAgreementStatus.OFFER_RESTRICTION_IN_PROGRESS && (
              <StatusBarV4
                backgroundColor={StatusBarColorsV4.YELLOW}
                status={microsoftStatus}
                statusColor={StatusBarStatusColorsV4.YELLOW}
                divider
                content={
                  'Deactivation is initiated, will be deactivated in 5 min.'
                }
              />
            )}
            {offerStatus === ResellerAgreementStatus.OFFER_RESTRICTED && (
              <StatusBarV4
                backgroundColor={StatusBarColorsV4.RED}
                status={microsoftStatus}
                divider
                statusColor={StatusBarStatusColorsV4.RED}
                content={`Deactivated on ${offerRestrictedDate}.`}
              />
            )}
            {offerStatus === ResellerAgreementStatus.OFFER_EXPIRED && (
              <StatusBarV4
                backgroundColor={StatusBarColorsV4.RED}
                status={microsoftStatus}
                divider
                statusColor={StatusBarStatusColorsV4.RED}
                content={`Offer expired on ${offerExpiredDate}.`}
              />
            )}
            {offerExpiringSoon && (
              <StatusBarV4
                backgroundColor={StatusBarColorsV4.YELLOW}
                status={'Expiring soon'}
                divider
                statusColor={StatusBarStatusColorsV4.YELLOW}
                content={
                  isOneDayLeft
                    ? 'Offer expires today.'
                    : `Offer expiring in ${dayDiff} days.
                Expires at ${acceptByDate}.`
                }
              />
            )}
          </>
        )}
      </div>
      <ResellerAuthorizationHeaderActions
        resellerAuthorization={resellerAuthorization}
        crmOpportunityId={crmOpportunityId}
        offerExpiringSoon={offerExpiringSoon}
        externalBaseURL={externalBaseURL}
        search={search}
      />
    </div>
  )
}
